import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PerformanceTable from "./PerformanceTable";
import { Box, Grid } from "@mui/system";
import SquareIcon from "@mui/icons-material/Square";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  selectBizDashboardLoading,
  selectBizOutputMetadata,
  selectBizQueryOutput,
  selectBizComparisonQueryOutput,
  selectBizComparisonLoading,
  selectBizIsPerformanceApiCalled,
  selectBizPerformanceBrandSearch,
  setIsPerformanceApiCalled,
  setPerformanceBrandSearch,
} from "../../../../../features/bizDevelopmentDashboardSlice";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { selectUserData } from "../../../../../features/userSlice";
import { DeepViewEndPointLogic } from "../../../../../logic/DeepView/DeepViewEndPointLogic";
import {
  buildColumns,
  exportToCSV,
} from "../../helpers/bizDevelopmentDashboardHelpers";
import FloatingActionItems from "../../../../../pages/FloatingItems/FloatingActionItems";
import TimeFrameCompareControl, {
  CommittedSelection,
  CompareTimeframeValue,
  PrimaryTimeframeValue,
  getCompareLabel,
  getPrimaryLabel,
} from "./TimeFrameCompareControl";

const PRIMARY_VALUES: PrimaryTimeframeValue[] = [
  "yesterday",
  "two_days_ago",
  "three_days_ago",
  "mtd",
  "custom",
];
const COMPARE_VALUES: CompareTimeframeValue[] = [
  "yesterday",
  "two_days_ago",
  "three_days_ago",
];

const Performance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const queryOutput = useSelector(selectBizQueryOutput);
  const outputMetadata = useSelector(selectBizOutputMetadata);
  const isLoading = useSelector(selectBizDashboardLoading);
  const comparisonQueryOutput = useSelector(selectBizComparisonQueryOutput);
  const isComparisonLoading = useSelector(selectBizComparisonLoading);
  const isPerformanceApiCalled = useSelector(selectBizIsPerformanceApiCalled);
  const oauthTokenHeader = useSelector(selectUserData)?.data?.oauth_token;
  const {
    fetchDashboardData,
    fetchComparisonData,
    clearComparisonData,
    getSitesFromParams,
  } = useBizDevelopmentDashboard();

  const buildCustomRange = (
    start: dayjs.Dayjs | null,
    end: dayjs.Dayjs | null
  ) =>
    start && end
      ? {
          startDate: start.format("YYYY-MM-DD"),
          endDate: end.format("YYYY-MM-DD"),
        }
      : undefined;

  const [committed, setCommitted] = useState<CommittedSelection>(() => {
    const primaryParam =
      searchParams.get("timeframePerformance") ?? "yesterday";
    const primary = (PRIMARY_VALUES as string[]).includes(primaryParam)
      ? (primaryParam as PrimaryTimeframeValue)
      : "yesterday";
    const customStartParam = searchParams.get("customStartPerformance");
    const customEndParam = searchParams.get("customEndPerformance");
    const compareParam = searchParams.get("additionalTimeframePerformance");
    const compare = (COMPARE_VALUES as string[]).includes(compareParam ?? "")
      ? (compareParam as CompareTimeframeValue)
      : null;
    return {
      primary,
      customStart:
        primary === "custom" && customStartParam
          ? dayjs(customStartParam)
          : null,
      customEnd:
        primary === "custom" && customEndParam ? dayjs(customEndParam) : null,
      compareEnabled: primary !== "mtd" && primary !== "custom" && !!compare,
      compare,
    };
  });

  // Currencies used by the Deep View drawer opened from the Performance table
  useEffect(() => {
    DeepViewEndPointLogic.getCurrenciesInDeepView(oauthTokenHeader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPerformanceApiCalled) return;
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    fetchDashboardData(
      committed.primary,
      sites,
      buildCustomRange(committed.customStart, committed.customEnd)
    );
    dispatch(setIsPerformanceApiCalled(true));
    if (committed.compareEnabled && committed.compare) {
      fetchComparisonData(committed.compare, sites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerformanceApiCalled]);

  const handleApply = (next: CommittedSelection) => {
    const primaryChanged =
      next.primary !== committed.primary ||
      next.customStart?.valueOf() !== committed.customStart?.valueOf() ||
      next.customEnd?.valueOf() !== committed.customEnd?.valueOf();
    const compareChanged =
      next.compareEnabled !== committed.compareEnabled ||
      next.compare !== committed.compare;

    setCommitted(next);
    if (primaryChanged) {
      dispatch(setPerformanceBrandSearch(""));
    }
    setSearchParams((prev) => {
      prev.set("timeframePerformance", next.primary);
      if (next.primary === "custom" && next.customStart && next.customEnd) {
        prev.set(
          "customStartPerformance",
          next.customStart.format("YYYY-MM-DD")
        );
        prev.set("customEndPerformance", next.customEnd.format("YYYY-MM-DD"));
      } else {
        prev.delete("customStartPerformance");
        prev.delete("customEndPerformance");
      }
      if (next.compareEnabled && next.compare) {
        prev.set("additionalTimeframePerformance", next.compare);
      } else {
        prev.delete("additionalTimeframePerformance");
      }
      if (primaryChanged) prev.delete("brandSearchPerformance");
      return prev;
    });

    const sites = getSitesFromParams();
    if (primaryChanged) {
      fetchDashboardData(
        next.primary,
        sites,
        buildCustomRange(next.customStart, next.customEnd)
      );
      dispatch(setIsPerformanceApiCalled(true));
    }

    if (next.compareEnabled && next.compare) {
      if (compareChanged) {
        fetchComparisonData(next.compare, sites);
      }
    } else {
      clearComparisonData();
    }
  };

  const brandSearch = useSelector(selectBizPerformanceBrandSearch);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  // Initialise Redux from URL param on first mount (handles page refresh)
  useEffect(() => {
    const saved = searchParams.get("brandSearchPerformance");
    if (saved && saved !== brandSearch) {
      dispatch(setPerformanceBrandSearch(saved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brandNameOptions = useMemo(
    () =>
      Array.from(
        new Set(
          queryOutput
            .map((r: Record<string, any>) => String(r.brand ?? ""))
            .filter(Boolean)
        )
      ).sort(),
    [queryOutput]
  );

  const filteredQueryOutput = useMemo(() => {
    if (!brandSearch.trim()) return queryOutput;
    const q = brandSearch.trim().toLowerCase();
    return queryOutput.filter((r: Record<string, any>) =>
      String(r.brand ?? "")
        .toLowerCase()
        .includes(q)
    );
  }, [queryOutput, brandSearch]);

  const filteredComparisonOutput = useMemo(() => {
    if (!brandSearch.trim()) return comparisonQueryOutput;
    const q = brandSearch.trim().toLowerCase();
    return (comparisonQueryOutput ?? []).filter((r: Record<string, any>) =>
      String(r.brand ?? "")
        .toLowerCase()
        .includes(q)
    );
  }, [comparisonQueryOutput, brandSearch]);

  const isMtd = searchParams.get("timeframePerformance") === "mtd";

  const isComparisonActive = committed.compareEnabled && !!committed.compare;

  const handleDownload = () => {
    const columns = buildColumns(outputMetadata, isMtd);

    exportToCSV({
      columns,
      primaryRows: queryOutput,
      comparisonRows: isComparisonActive ? comparisonQueryOutput ?? [] : [],
      primaryLabel: getPrimaryLabel(
        committed.primary,
        committed.customStart,
        committed.customEnd
      ),
      comparisonLabel: isComparisonActive
        ? getCompareLabel(committed.compare)
        : "",
      isMtd,
      filename: `performance_${committed.primary}${
        isComparisonActive ? `_vs_${committed.compare}` : ""
      }`,
    });
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
        {/* Brand Name — left */}
        <Grid size={3}>
          <Autocomplete
            freeSolo
            forcePopupIcon
            disabled={
              queryOutput.length === 0 || isLoading || isComparisonLoading
            }
            open={brandDropdownOpen}
            onOpen={() => setBrandDropdownOpen(true)}
            onClose={() => setBrandDropdownOpen(false)}
            options={brandNameOptions}
            inputValue={brandSearch}
            onInputChange={(_, val, reason) => {
              dispatch(setPerformanceBrandSearch(val));
              setSearchParams((prev) => {
                if (val) prev.set("brandSearchPerformance", val);
                else prev.delete("brandSearchPerformance");
                return prev;
              });
              if (reason === "input") setBrandDropdownOpen(false);
            }}
            onChange={(_, val) => {
              const v = typeof val === "string" ? val : "";
              dispatch(setPerformanceBrandSearch(v));
              setBrandDropdownOpen(false);
              setSearchParams((prev) => {
                if (v) prev.set("brandSearchPerformance", v);
                else prev.delete("brandSearchPerformance");
                return prev;
              });
            }}
            size="small"
            sx={{ pt: "8px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                color="secondary"
                label="Brand Name"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: "rgba(0,0,0,0.54)", ml: "12px" }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* Spacer — pushes the timeframe controls to the right */}
        <Grid size="grow" />

        {/* Time Frame + Compare */}
        <Grid size={4}>
          <TimeFrameCompareControl
            value={committed}
            onApply={handleApply}
            hasData={queryOutput.length > 0}
            disabled={isLoading || isComparisonLoading}
            dataTracker="799"
          />
        </Grid>
        <Grid size="auto">
          <Box display={"flex"} justifyContent={"flex-end"}>
            <IconButton
              aria-label="refresh"
              color="secondary"
              disabled={isLoading || isComparisonLoading}
              onClick={() => {
                const sites = getSitesFromParams();
                fetchDashboardData(
                  committed.primary,
                  sites,
                  buildCustomRange(committed.customStart, committed.customEnd)
                );
                dispatch(setIsPerformanceApiCalled(true));
                if (committed.compareEnabled && committed.compare) {
                  fetchComparisonData(committed.compare, sites);
                }
              }}
              data-tracker="802"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="secondary"
              sx={{ ml: 2 }}
              disabled={
                isLoading || isComparisonLoading || queryOutput.length === 0
              }
              onClick={handleDownload}
              data-tracker="801"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Grid>
        {isComparisonActive && (
          <Grid size={12}>
            <Box display={"flex"} alignItems={"center"}>
              <Typography
                display={"flex"}
                alignItems={"center"}
                fontSize={"12px"}
                mr={2}
              >
                <SquareIcon
                  sx={{
                    color: "#000000DE",
                    mr: 0.2,
                    fontSize: "20px",
                    borderRadius: "16px",
                  }}
                />
                {getPrimaryLabel(
                  committed.primary,
                  committed.customStart,
                  committed.customEnd
                )}
              </Typography>
              <Typography
                display={"flex"}
                alignItems={"center"}
                fontSize={"12px"}
              >
                <SquareIcon
                  sx={{
                    color: "#00000061",
                    mr: 0.2,
                    fontSize: "20px",
                    borderRadius: "16px",
                  }}
                />
                {getCompareLabel(committed.compare)}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
      <PerformanceTable
        outputMetadata={outputMetadata}
        queryOutput={filteredQueryOutput}
        isLoading={isLoading}
        comparisonQueryOutput={
          isComparisonActive ? filteredComparisonOutput : []
        }
        isComparisonLoading={isComparisonLoading}
        isComparisonActive={isComparisonActive}
      />
      <div data-tracker="803">
        <FloatingActionItems
          selectedSiteFloatingAction={getSitesFromParams().join(",")}
          selectedDimensionsFloatingAction=""
          trackIdFloatingAction="804"
          disabledButton={queryOutput.length === 0}
        />
      </div>
    </>
  );
};

export default Performance;
